import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import {
  Bottles,
  CheckIns,
  Locations,
  MarketValue,
  Pricing,
  type Bottle,
  type BottleStatus,
  type CheckIn,
  type TastingNote,
} from '@/lib/api';
import { Button, Card, EmptyState, Icon, IconButton, Input, Modal, Pill, Tabs, TableHead, Chip, Skeleton } from '@/design-system';
import { BottleCard, BottleCardSkeleton, BottleImage } from '@/components/BottleCard';
import { useDebounce } from '@/lib/hooks';
import { useToasts } from '@/lib/store';
import { AddBottleDrawer } from '@/components/AddBottleDrawer';
import { BottlePricingDrawer } from '@/components/BottlePricingDrawer';
import { SellBottleModal } from '@/components/SellBottleModal';
import { LocationTree, useSelectionLabel, type LocationSelection } from '@/components/LocationTree';
import { LocationFormModal } from '@/components/LocationFormModal';
import { LocationPicker } from '@/components/LocationPicker';
import { flattenLocationTree } from '@/lib/locations';
import { formatPriceNok } from '@/lib/format';
import { styleChipColor, styleChipLabel } from '@/lib/style-chip';

type SortKey = 'addedDesc' | 'addedAsc' | 'nameAsc' | 'vintageDesc';
type ViewMode = 'table' | 'cards';

import type { BottleCategory } from '@/lib/api';

const CATEGORY_PILLS: { value: BottleCategory | 'all'; label: string; icon: 'sparkles' | 'bottle' | 'beer' | 'spirits' }[] = [
  { value: 'all', label: 'Alle', icon: 'sparkles' },
  { value: 'wine', label: 'Vin', icon: 'bottle' },
  { value: 'beer', label: 'Øl', icon: 'beer' },
  { value: 'spirits', label: 'Sprit', icon: 'spirits' },
];

const isCategory = (v: string | null): v is BottleCategory =>
  v === 'wine' || v === 'beer' || v === 'spirits' || v === 'cider' || v === 'sake' || v === 'mead' || v === 'other';

const STATUS_TABS: { id: BottleStatus | 'all'; key: string }[] = [
  { id: 'all', key: 'cellar.filters.all' },
  { id: 'cellared', key: 'cellar.tabs.cellared' },
  { id: 'drunk', key: 'cellar.tabs.drunk' },
  { id: 'gifted', key: 'cellar.tabs.gifted' },
];

const COMMON_GRAPES = ['Pinot Noir', 'Chardonnay', 'Riesling', 'Sangiovese', 'Tempranillo', 'Cabernet'];
const PAGE_SIZE = 24;

const SORT_OPTIONS: { value: SortKey; labelKey: string }[] = [
  { value: 'addedDesc', labelKey: 'cellar.sort.addedDesc' },
  { value: 'addedAsc', labelKey: 'cellar.sort.addedAsc' },
  { value: 'nameAsc', labelKey: 'cellar.sort.nameAsc' },
  { value: 'vintageDesc', labelKey: 'cellar.sort.vintageDesc' },
];

const isSortKey = (v: string | null): v is SortKey =>
  v === 'addedDesc' || v === 'addedAsc' || v === 'nameAsc' || v === 'vintageDesc';

const isStatus = (v: string | null): v is BottleStatus | 'all' =>
  v === 'all' || v === 'cellared' || v === 'drunk' || v === 'gifted';

/** Encodes/decodes the sidebar selection into a single `loc` URL param. */
function selectionToParam(selection: LocationSelection): string | undefined {
  if (selection.kind === 'all') return undefined;
  if (selection.kind === 'location') return selection.id;
  return selection.kind; // 'unassigned' | 'pending'
}
function paramToSelection(value: string | null): LocationSelection {
  if (!value || value === 'all') return { kind: 'all' };
  if (value === 'unassigned') return { kind: 'unassigned' };
  if (value === 'pending') return { kind: 'pending' };
  return { kind: 'location', id: value };
}

export function CellarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [params, setParams] = useSearchParams();

  // Read URL state
  const q = params.get('q') ?? '';
  const status: BottleStatus | 'all' = isStatus(params.get('status')) ? (params.get('status') as BottleStatus | 'all') : 'cellared';
  const category: BottleCategory | 'all' = isCategory(params.get('category')) ? (params.get('category') as BottleCategory) : 'all';
  const grape = params.get('grape') ?? undefined;
  const region = params.get('region') ?? undefined;
  const sort: SortKey = isSortKey(params.get('sort')) ? (params.get('sort') as SortKey) : 'addedDesc';
  const drinkBy = params.get('drinkBy') ? Number(params.get('drinkBy')) : undefined;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const rawView = params.get('view');
  const view: ViewMode = rawView === 'cards' || rawView === 'grid' ? 'cards' : 'table';
  const selection = paramToSelection(params.get('loc'));

  const [searchInput, setSearchInput] = useState(q);
  const [addOpen, setAddOpen] = useState(false);
  const [pricingBottleId, setPricingBottleId] = useState<string | undefined>(undefined);
  const [pricingTab, setPricingTab] = useState<'pricing' | 'quantity' | 'details'>('pricing');
  const [sellBottle, setSellBottle] = useState<Bottle | undefined>(undefined);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [mobileLocationOpen, setMobileLocationOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 280);
  // Mobile: collapsible toolbars. Search auto-opens if user already has a query.
  const [searchOpenMobile, setSearchOpenMobile] = useState(Boolean(q));
  const [filtersOpenMobile, setFiltersOpenMobile] = useState(false);

  // Sync debounced search → URL (drops to page 1)
  useEffect(() => {
    if (debouncedSearch === q) return;
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('q', debouncedSearch);
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const updateParam = (key: string, value: string | undefined, opts: { resetPage?: boolean } = { resetPage: true }) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === '' || value === null) next.delete(key);
    else next.set(key, value);
    if (opts.resetPage) next.delete('page');
    setParams(next, { replace: true });
  };

  const overviewQ = useQuery({ queryKey: ['locations.tree'], queryFn: () => Locations.tree() });

  const selectedNode = useMemo(() => {
    if (selection.kind !== 'location' || !overviewQ.data) return undefined;
    return findNode(overviewQ.data.tree, selection.id);
  }, [selection, overviewQ.data]);

  // Location-derived filter params — a Room/Cabinet selection expands to its
  // whole subtree (computed from the tree we already have client-side).
  const locationFilter = useMemo(() => {
    if (selection.kind === 'unassigned') return { unassigned: true as const };
    if (selection.kind === 'pending') return { pendingDelivery: true as const };
    if (selection.kind === 'location' && selectedNode) {
      return { locationIds: collectIds(selectedNode).join(',') };
    }
    return {};
  }, [selection, selectedNode]);

  const baseFilters = {
    q: q || undefined,
    status: status === 'all' ? undefined : status,
    category: category === 'all' ? undefined : category,
    grape,
    region,
    drinkBy,
    ...locationFilter,
  };

  const bottlesQ = useQuery({
    queryKey: ['cellar.bottles', { ...baseFilters, sort, page }],
    queryFn: () =>
      Bottles.list({
        ...baseFilters,
        sort: sort.startsWith('added') ? 'addedAt' : sort.startsWith('vintage') ? 'vintage' : 'name',
        order: sort.endsWith('Asc') ? 'asc' : 'desc',
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const statsQ = useQuery({
    queryKey: ['cellar.stats', baseFilters],
    queryFn: () => Bottles.stats(baseFilters),
  });

  // Self-owned market value ("Markedsverdi" tile) — catalog price × qty for
  // matched bottles vs. the purchase-value baseline. See
  // services/market-value-service.ts + routes/market-value.ts.
  const marketValueQ = useQuery({ queryKey: ['market-value.cellar'], queryFn: () => MarketValue.forCellar() });

  // Market reference price — reuses the Prisautomat overview join rather
  // than a second bottle→catalog matcher. Only needed for the table's
  // MARKED column, but cheap enough to always fetch alongside.
  const pricingOverviewQ = useQuery({ queryKey: ['pricing.overview'], queryFn: () => Pricing.overview(), staleTime: 30_000 });
  const marketPriceByBottleId = useMemo(() => {
    const map = new Map<string, number | undefined>();
    for (const row of pricingOverviewQ.data?.bottles ?? []) map.set(row.bottle.id, row.catalogPriceNok);
    return map;
  }, [pricingOverviewQ.data]);

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of flattenLocationTree(overviewQ.data?.tree ?? [])) map.set(opt.id, opt.name);
    return map;
  }, [overviewQ.data]);

  const bottles = useMemo(() => bottlesQ.data?.bottles ?? [], [bottlesQ.data]);
  const total = bottlesQ.data?.meta?.total ?? bottles.length;
  const limit = bottlesQ.data?.meta?.limit ?? PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isFetching = bottlesQ.isFetching;
  const selectionLabel = useSelectionLabel(overviewQ.data, selection);

  // Reset selection when leaving select mode
  useEffect(() => {
    if (!selectMode) setSelectedIds(new Set());
  }, [selectMode]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const b of bottles) next.add(b.id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const invalidateCellar = () => {
    void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
    void qc.invalidateQueries({ queryKey: ['cellar.stats'] });
    void qc.invalidateQueries({ queryKey: ['locations.tree'] });
  };

  const bulk = useMutation({
    mutationFn: (input: { op: 'move-location' | 'delete' | 'tag'; payload: Record<string, unknown> }) =>
      Bottles.bulk(input.op, [...selectedIds], input.payload),
    onSuccess: (result, variables) => {
      const affected = bulkAffectedCount(result, selectedIds.size);
      const verb = variables.op === 'delete' ? 'fjernet' : variables.op === 'move-location' ? 'flyttet' : 'tagget';
      push({ title: `${affected} flasker ${verb}`, tone: 'success' });
      invalidateCellar();
      clearSelection();
      setSelectMode(false);
      setMoveOpen(false);
      setTagOpen(false);
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const setQty = useMutation({
    mutationFn: (input: { bottle: Bottle; qty: number }) => Bottles.update(input.bottle.id, { qty: input.qty }, input.bottle.etag),
    onSuccess: invalidateCellar,
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const clearFilters = () => {
    const next = new URLSearchParams();
    next.set('status', 'cellared');
    setParams(next, { replace: true });
    setSearchInput('');
  };
  const filterCount = [grape, region, drinkBy, selection.kind !== 'all' ? 1 : undefined].filter(Boolean).length;
  const selectedCount = selectedIds.size;
  const checkInsQ = useQuery({
    queryKey: ['checkins.mine.overview'],
    queryFn: () => CheckIns.list({ scope: 'mine', limit: 200 }),
    staleTime: 60_000,
  });
  const noteQueries = useQueries({
    queries: bottles.map((b) => ({
      queryKey: ['bottle.notes.summary', b.id],
      queryFn: () => Bottles.notes(b.id),
      enabled: Boolean(b.id) && view === 'cards',
      staleTime: 60_000,
    })),
  });
  const tasteByBottleId = buildTasteSummaries(bottles, noteQueries.map((q) => q.data), checkInsQ.data ?? []);

  const openPricing = (id: string, tab: 'pricing' | 'quantity' | 'details' = 'pricing') => {
    setPricingTab(tab);
    setPricingBottleId(id);
  };

  return (
    <div className="grid gap-4 lg:grid lg:grid-cols-[220px_1fr] lg:items-start lg:gap-6">
      {/* Desktop sidebar */}
      <aside className="hidden lg:sticky lg:top-20 lg:block">
        <Card className="max-h-[calc(100vh-6rem)] overflow-y-auto p-3">
          <LocationTree
            overview={overviewQ.data}
            loading={overviewQ.isLoading}
            selected={selection}
            onSelect={(sel) => updateParam('loc', selectionToParam(sel))}
            onCreateLocation={() => setNewLocationOpen(true)}
          />
        </Card>
      </aside>

      <div className="grid gap-4 lg:gap-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="ui-section-title text-2xl lg:text-3xl">{t('cellar.title')}</h1>
            <p className="text-xs text-mist lg:text-sm">
              {selectionLabel} · {t('cellar.subtitle', { count: total, sort: t(`cellar.sort.${sort}`) })}
            </p>
          </div>
          <div className="hidden items-center gap-2 lg:flex">
            <Tabs<ViewMode>
              tabs={[
                { id: 'table', label: 'Tabell', icon: 'list' },
                { id: 'cards', label: 'Kort', icon: 'grid' },
              ]}
              active={view}
              onChange={(v) => updateParam('view', v === 'table' ? undefined : v, { resetPage: false })}
            />
            <Button
              variant={selectMode ? 'primary' : 'ghost'}
              iconLeft={selectMode ? 'check' : 'list'}
              onClick={() => setSelectMode((s) => !s)}
            >
              {selectMode ? `Velg (${selectedCount})` : 'Velg flere'}
            </Button>
            <Button variant="ghost" iconLeft="plus" onClick={() => setNewLocationOpen(true)}>
              Ny lokasjon
            </Button>
            <Button variant="primary" iconLeft="add-bottle" onClick={() => setAddOpen(true)}>
              {t('cellar.addBottle')}
            </Button>
          </div>
        </header>

        {/* Stats header — Vinify-style totals for the current selection */}
        <StatsHeader
          stats={statsQ.data}
          loading={statsQ.isLoading}
          marketValueNok={marketValueQ.data?.totalMarketValueNok}
          marketValueLoading={marketValueQ.isLoading}
        />

        {/* Mobile compact toolbar */}
        <div className="flex items-center gap-2 lg:hidden">
          <IconButton
            icon="search"
            label="Søk"
            variant={searchOpenMobile || q ? 'primary' : 'ghost'}
            onClick={() => setSearchOpenMobile((v) => !v)}
          />
          <button
            type="button"
            onClick={() => setMobileLocationOpen(true)}
            className={clsx('ui-btn ui-btn--sm', selection.kind !== 'all' ? 'ui-btn--primary' : 'ui-btn--ghost')}
          >
            <Icon name="door" size={14} />
            <span className="max-w-[90px] truncate">{selectionLabel}</span>
          </button>
          <button
            type="button"
            onClick={() => setFiltersOpenMobile((v) => !v)}
            className={clsx(
              'ui-btn ui-btn--sm',
              filtersOpenMobile || filterCount > 0 ? 'ui-btn--primary' : 'ui-btn--ghost',
            )}
            aria-label="Filtre"
          >
            <Icon name="filter-list" size={14} />
            Filtre{filterCount > 0 ? ` (${filterCount})` : ''}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <IconButton
              icon={view === 'table' ? 'grid' : 'list'}
              label={view === 'table' ? 'Kortvisning' : 'Tabellvisning'}
              onClick={() => updateParam('view', view === 'table' ? 'cards' : undefined, { resetPage: false })}
            />
            <IconButton
              icon="add-bottle"
              label={t('cellar.addBottle')}
              variant="primary"
              onClick={() => setAddOpen(true)}
            />
          </div>
        </div>

        {/* Search + filters (collapsible on mobile, always visible on desktop) */}
        <div className="grid gap-3">
          <div
            className={clsx(
              'grid gap-3 lg:grid lg:grid-cols-[1fr_auto_auto] lg:items-center',
              searchOpenMobile ? 'block' : 'hidden lg:grid',
            )}
          >
            <div className="relative">
              <Input
                iconLeft="search"
                placeholder={t('cellar.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {isFetching && (
                <Icon name="spinner" size={14} className="absolute right-3 top-3.5 animate-spin text-mist" />
              )}
            </div>
            <select
              value={sort}
              onChange={(e) => updateParam('sort', e.target.value)}
              aria-label="Sorter"
              className="h-10 w-full rounded-md border border-line bg-ink-soft px-3 text-sm text-parchment focus:border-candle focus:outline-none lg:min-w-[180px] lg:w-auto"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey, { defaultValue: opt.value })}
                </option>
              ))}
            </select>
            <div className="-mx-1 overflow-x-auto px-1 lg:mx-0 lg:px-0">
              <Tabs
                tabs={STATUS_TABS.map((s) => ({ id: s.id, label: t(s.key) }))}
                active={status}
                onChange={(id) => updateParam('status', id === 'cellared' ? undefined : id)}
              />
            </div>
          </div>

          {/* Status tabs always visible on mobile, even when search is collapsed */}
          {!searchOpenMobile && (
            <div className="-mx-1 overflow-x-auto px-1 lg:hidden">
              <Tabs
                tabs={STATUS_TABS.map((s) => ({ id: s.id, label: t(s.key) }))}
                active={status}
                onChange={(id) => updateParam('status', id === 'cellared' ? undefined : id)}
              />
            </div>
          )}

          {/* Filter pills row */}
          <div
            className={clsx(
              'flex flex-wrap items-center gap-2',
              filtersOpenMobile ? 'flex' : 'hidden lg:flex',
            )}
          >
            {/* Category — wine / beer / spirits */}
            {CATEGORY_PILLS.map((c) => (
              <Pill
                key={c.value}
                active={category === c.value}
                icon={c.icon}
                onClick={() => updateParam('category', c.value === 'all' ? undefined : c.value)}
              >
                {c.label}
              </Pill>
            ))}
            {COMMON_GRAPES.map((g) => (
              <Pill
                key={g}
                active={grape === g}
                icon="grape"
                onClick={() => updateParam('grape', grape === g ? undefined : g)}
              >
                {g}
              </Pill>
            ))}
            <Pill
              icon="clock"
              active={drinkBy != null}
              onClick={() =>
                updateParam('drinkBy', drinkBy ? undefined : String(new Date().getFullYear() + 1))
              }
            >
              Drikkes snart
            </Pill>
            {filterCount > 0 && (
              <button
                onClick={clearFilters}
                className="ml-auto text-xs uppercase tracking-widest text-mist hover:text-candle"
              >
                Nullstill {filterCount} filter
              </button>
            )}
          </div>
        </div>

        {/* Bulk action bar */}
        {selectMode && (
          <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-md border border-candle/40 bg-ink/95 p-3 backdrop-blur-sm">
            <span className="text-sm text-parchment">
              {selectedCount === 0 ? 'Ingen valgte' : `${selectedCount} valgte`}
            </span>
            <Button size="sm" variant="ghost" iconLeft="check" onClick={selectAllVisible} disabled={bottles.length === 0}>
              Velg alle synlige
            </Button>
            <Button size="sm" variant="ghost" iconLeft="x" onClick={clearSelection} disabled={selectedCount === 0}>
              Nullstill
            </Button>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                iconLeft="door"
                disabled={selectedCount === 0}
                onClick={() => setMoveOpen(true)}
              >
                Flytt til lokasjon
              </Button>
              <Button
                size="sm"
                variant="ghost"
                iconLeft="tag"
                disabled={selectedCount === 0}
                onClick={() => setTagOpen(true)}
              >
                Tag
              </Button>
              <Button
                size="sm"
                variant="ghost"
                iconLeft="trash"
                disabled={selectedCount === 0}
                loading={bulk.isPending && bulk.variables?.op === 'delete'}
                onClick={() => {
                  if (window.confirm(`Slette ${selectedCount} flasker?`)) {
                    bulk.mutate({ op: 'delete', payload: {} });
                  }
                }}
              >
                Slett
              </Button>
            </div>
          </div>
        )}

        {/* content */}
        {bottlesQ.isLoading ? (
          view === 'cards' ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <BottleCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <Skeleton height={420} rounded="md" />
          )
        ) : bottlesQ.isError ? (
          <EmptyState
            icon="warning"
            title={t('common.error')}
            description="Vi får ikke kontakt med kjelleren akkurat nå."
            action={
              <Button variant="ghost" iconLeft="refresh" onClick={() => bottlesQ.refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : bottles.length === 0 ? (
          <EmptyState
            title={t('cellar.empty')}
            description={t('cellar.emptyHint')}
            action={
              <div className="flex gap-2">
                <Button variant="primary" iconLeft="add-bottle" onClick={() => setAddOpen(true)}>
                  {t('cellar.addBottle')}
                </Button>
                <Button variant="ghost" iconLeft="scan" onClick={() => (window.location.href = '/scan')}>
                  Skann
                </Button>
              </div>
            }
          />
        ) : view === 'cards' ? (
          <motion.div layout className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {bottles.map((b) => {
              const taste = tasteByBottleId.get(b.id);
              return (
                <BottleCard
                  key={b.id}
                  bottle={b}
                  rating={taste?.averageRating}
                  ratingCount={taste?.ratingCount}
                  tasteNote={taste?.latestComment}
                  tastedWith={taste?.latestWithFood}
                  selectable={selectMode}
                  selected={selectedIds.has(b.id)}
                  onToggleSelect={() => toggleSelected(b.id)}
                  onOpenPricing={selectMode ? undefined : (bottle) => openPricing(bottle.id)}
                />
              );
            })}
          </motion.div>
        ) : (
          <CellarTable
            bottles={bottles}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelected}
            onSetQty={(bottle, qty) => setQty.mutate({ bottle, qty })}
            marketPriceByBottleId={marketPriceByBottleId}
            locationNameById={locationNameById}
            onOpenPricing={(id) => openPricing(id, 'pricing')}
            onOpenLocation={(id) => openPricing(id, 'quantity')}
            onOpenNote={(id) => openPricing(id, 'details')}
            onSell={setSellBottle}
          />
        )}

        {/* Pagination */}
        {!bottlesQ.isLoading && total > limit && (
          <div className="flex items-center justify-between gap-3 pt-4">
            <span className="text-xs uppercase tracking-widest text-mist">
              Side {page} av {totalPages} · {total} flasker
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                iconLeft="chevron-left"
                disabled={page <= 1}
                onClick={() => updateParam('page', page > 2 ? String(page - 1) : undefined, { resetPage: false })}
              >
                Forrige
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconRight="chevron-right"
                disabled={page >= totalPages}
                onClick={() => updateParam('page', String(page + 1), { resetPage: false })}
              >
                Neste
              </Button>
            </div>
          </div>
        )}

        <AddBottleDrawer open={addOpen} onClose={() => setAddOpen(false)} />

        <BottlePricingDrawer
          open={Boolean(pricingBottleId)}
          bottleId={pricingBottleId}
          initialTab={pricingTab}
          onClose={() => setPricingBottleId(undefined)}
        />

        <SellBottleModal open={Boolean(sellBottle)} bottle={sellBottle} onClose={() => setSellBottle(undefined)} />

        <LocationFormModal open={newLocationOpen} onClose={() => setNewLocationOpen(false)} />

        {/* Bulk move-to-location modal */}
        <BulkMoveLocationModal
          open={moveOpen}
          onClose={() => setMoveOpen(false)}
          loading={bulk.isPending}
          onConfirm={(locationId) => bulk.mutate({ op: 'move-location', payload: { locationId } })}
        />

        {/* Bulk tag modal */}
        <BulkTagModal
          open={tagOpen}
          onClose={() => setTagOpen(false)}
          loading={bulk.isPending}
          onConfirm={(tags) => bulk.mutate({ op: 'tag', payload: { tags } })}
        />
      </div>

      {/* Mobile location sheet */}
      <Modal open={mobileLocationOpen} onClose={() => setMobileLocationOpen(false)} title="Lokasjon" size="sm">
        <LocationTree
          overview={overviewQ.data}
          loading={overviewQ.isLoading}
          selected={selection}
          onSelect={(sel) => {
            updateParam('loc', selectionToParam(sel));
            setMobileLocationOpen(false);
          }}
          onCreateLocation={() => {
            setMobileLocationOpen(false);
            setNewLocationOpen(true);
          }}
        />
      </Modal>
    </div>
  );
}

function StatsHeader({
  stats,
  loading,
  marketValueNok,
  marketValueLoading,
}: {
  stats?: { bottleCount: number; totalQty: number; purchaseValueNok: number; sellingValueNok: number };
  loading: boolean;
  marketValueNok?: number;
  marketValueLoading: boolean;
}) {
  return (
    <Card className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
      <StatTile label="Viner" value={loading ? undefined : String(stats?.bottleCount ?? 0)} />
      <StatTile label="Flasker" value={loading ? undefined : String(stats?.totalQty ?? 0)} />
      <StatTile label="Kjøpsverdi" value={loading ? undefined : formatPriceNok(stats?.purchaseValueNok ?? 0)} />
      <StatTile label="Salgsverdi" value={loading ? undefined : formatPriceNok(stats?.sellingValueNok ?? 0)} />
      <StatTile label="Markedsverdi" value={marketValueLoading ? undefined : formatPriceNok(marketValueNok ?? 0)} />
    </Card>
  );
}

function StatTile({ label, value }: { label: string; value?: string }) {
  return (
    <div className="grid gap-1">
      <span className="text-[10px] uppercase tracking-widest text-mist">{label}</span>
      {value === undefined ? (
        <Skeleton height={22} width={64} />
      ) : (
        <span className="font-serif text-xl text-parchment">{value}</span>
      )}
    </div>
  );
}

const TABLE_COLUMNS = '24px minmax(180px,2.4fr) 100px 90px 90px 90px 90px 110px 120px 32px 32px';

function CellarTable({
  bottles,
  selectMode,
  selectedIds,
  onToggleSelect,
  onSetQty,
  marketPriceByBottleId,
  locationNameById,
  onOpenPricing,
  onOpenLocation,
  onOpenNote,
  onSell,
}: {
  bottles: Bottle[];
  selectMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSetQty: (bottle: Bottle, qty: number) => void;
  marketPriceByBottleId: Map<string, number | undefined>;
  locationNameById: Map<string, string>;
  onOpenPricing: (id: string) => void;
  onOpenLocation: (id: string) => void;
  onOpenNote: (id: string) => void;
  onSell: (bottle: Bottle) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 960 }}>
        <TableHead
          gridTemplateColumns={TABLE_COLUMNS}
          columns={[
            selectMode ? <span key="sel" /> : <span key="sel" />,
            'Produkt',
            'Antall',
            'Notat',
            'Kjøp',
            'Salg',
            'Marked',
            'Stil',
            'Plassering',
            '',
            '',
          ]}
        />
        <div className="grid gap-1 py-1">
          {bottles.map((bottle) => (
            <CellarTableRow
              key={bottle.id}
              bottle={bottle}
              selectMode={selectMode}
              selected={selectedIds.has(bottle.id)}
              onToggleSelect={() => onToggleSelect(bottle.id)}
              onSetQty={(qty) => onSetQty(bottle, qty)}
              marketPriceNok={marketPriceByBottleId.get(bottle.id)}
              locationName={bottle.locationId ? locationNameById.get(bottle.locationId) : undefined}
              onOpenPricing={() => onOpenPricing(bottle.id)}
              onOpenLocation={() => onOpenLocation(bottle.id)}
              onOpenNote={() => onOpenNote(bottle.id)}
              onSell={() => onSell(bottle)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CellarTableRow({
  bottle,
  selectMode,
  selected,
  onToggleSelect,
  onSetQty,
  marketPriceNok,
  locationName,
  onOpenPricing,
  onOpenLocation,
  onOpenNote,
  onSell,
}: {
  bottle: Bottle;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onSetQty: (qty: number) => void;
  marketPriceNok?: number;
  locationName?: string;
  onOpenPricing: () => void;
  onOpenLocation: () => void;
  onOpenNote: () => void;
  onSell: () => void;
}) {
  const archived = bottle.status !== 'cellared';
  const styleLabel = styleChipLabel(bottle);
  const styleColor = styleChipColor(styleLabel);

  const productCell = (
    <div className="flex min-w-0 items-center gap-3">
      <div className="relative grid h-16 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-ink-soft">
        <BottleImage src={bottle.photoUrl} iconSize={22} />
      </div>
      <span className="min-w-0">
        <span className="block truncate font-serif text-sm text-parchment">{bottle.name}</span>
        <span className="block truncate text-[10px] uppercase tracking-widest text-mist">
          {[bottle.vintage, bottle.producer].filter(Boolean).join(' · ')}
        </span>
      </span>
    </div>
  );

  return (
    <div style={{ gridTemplateColumns: TABLE_COLUMNS }} className="ui-sortable-row items-center py-2">
      {selectMode ? (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Velg ${bottle.name}`}
          className="h-4 w-4 accent-[color:var(--ui-color-ruby)]"
        />
      ) : (
        <span />
      )}
      {archived ? (
        <div className="opacity-70">{productCell}</div>
      ) : (
        <Link to={`/bottle/${bottle.id}`} className="min-w-0">
          {productCell}
        </Link>
      )}
      <div className="flex items-center gap-1.5">
        <IconButton
          icon="minus"
          label="Reduser antall"
          size="sm"
          disabled={bottle.qty <= 0 || archived}
          onClick={(e) => {
            e.preventDefault();
            onSetQty(Math.max(0, bottle.qty - 1));
          }}
        />
        <span className="w-5 text-center text-sm text-parchment">{bottle.qty}</span>
        <IconButton
          icon="plus"
          label="Øk antall"
          size="sm"
          disabled={archived}
          onClick={(e) => {
            e.preventDefault();
            onSetQty(bottle.qty + 1);
          }}
        />
      </div>
      <button
        type="button"
        title={bottle.notes || 'Ingen notat'}
        onClick={(e) => {
          e.preventDefault();
          onOpenNote();
        }}
        className="grid h-8 w-8 place-items-center justify-self-start text-mist hover:text-candle"
      >
        <Icon name={bottle.notes ? 'edit' : 'tag'} size={14} className={bottle.notes ? 'text-candle' : undefined} />
      </button>
      <span className="text-sm text-mist">{formatPriceNok(bottle.purchasePriceNok)}</span>
      <span className="text-sm text-parchment">{formatPriceNok(bottle.sellingPriceNok)}</span>
      <span className={clsx('text-sm', marketPriceNok != null ? 'text-candle' : 'text-mist')}>
        {marketPriceNok != null ? formatPriceNok(marketPriceNok) : '—'}
      </span>
      <Chip bg={styleColor.bg} fg={styleColor.fg}>{styleLabel}</Chip>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onOpenLocation();
        }}
        className="flex min-w-0 items-center gap-1 text-left text-xs text-parchment hover:text-candle"
      >
        {bottle.pendingDelivery ? (
          <>
            <Icon name="truck" size={12} className="shrink-0 text-mist" />
            <span className="truncate">Venter</span>
          </>
        ) : locationName ? (
          <span className="truncate">{locationName}</span>
        ) : (
          <span className="truncate text-mist">Uplassert</span>
        )}
      </button>
      <IconButton icon="receipt" label="Prising" size="sm" onClick={onOpenPricing} />
      <IconButton
        icon="external"
        label="Selg"
        size="sm"
        disabled={bottle.qty <= 0 || archived}
        onClick={(e) => {
          e.preventDefault();
          onSell();
        }}
      />
    </div>
  );
}

type TasteSummary = {
  averageRating?: number;
  ratingCount: number;
  latestComment?: string;
  latestWithFood?: string;
};

function buildTasteSummaries(
  bottles: Bottle[],
  notesByIndex: Array<TastingNote[] | undefined>,
  checkIns: CheckIn[],
): Map<string, TasteSummary> {
  const result = new Map<string, TasteSummary>();
  const checkInsByBottle = new Map<string, CheckIn[]>();
  for (const checkIn of checkIns) {
    if (!checkIn.bottleId) continue;
    const list = checkInsByBottle.get(checkIn.bottleId) ?? [];
    list.push(checkIn);
    checkInsByBottle.set(checkIn.bottleId, list);
  }

  bottles.forEach((bottle, index) => {
    const notes = notesByIndex[index] ?? [];
    const bottleCheckIns = checkInsByBottle.get(bottle.id) ?? [];
    const ratings = [
      ...notes.map((note) => note.rating1to5),
      ...bottleCheckIns.map((checkIn) => checkIn.rating).filter((rating): rating is number => rating != null),
    ];
    const latest = [
      ...notes.map((note) => ({
        createdAt: note.drunkAt ?? note.createdAt,
        comment: note.comment,
        withFood: note.withFood,
      })),
      ...bottleCheckIns.map((checkIn) => ({
        createdAt: checkIn.createdAt,
        comment: checkIn.comment,
        withFood: checkIn.withFood,
      })),
    ].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

    if (ratings.length === 0 && !latest?.comment && !latest?.withFood) return;
    result.set(bottle.id, {
      averageRating: ratings.length > 0
        ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
        : undefined,
      ratingCount: ratings.length,
      latestComment: latest?.comment,
      latestWithFood: latest?.withFood,
    });
  });

  return result;
}

function bulkAffectedCount(result: unknown, fallback: number): number {
  if (result && typeof result === 'object') {
    const data = result as { affected?: unknown; deleted?: unknown; bottles?: unknown };
    if (typeof data.affected === 'number') return data.affected;
    if (typeof data.deleted === 'number') return data.deleted;
    if (Array.isArray(data.bottles)) return data.bottles.length;
  }
  return fallback;
}

function findNode(nodes: import('@/lib/api').StorageLocationTreeNode[], id: string): import('@/lib/api').StorageLocationTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return undefined;
}

function collectIds(node: import('@/lib/api').StorageLocationTreeNode): string[] {
  return [node.id, ...node.children.flatMap(collectIds)];
}

function BulkMoveLocationModal({
  open,
  onClose,
  loading,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onConfirm: (locationId: string | null) => void;
}) {
  const [picked, setPicked] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (open) setPicked(undefined);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title="Flytt til lokasjon" size="sm">
      <div className="grid gap-4">
        <p className="text-sm text-mist">Velg lokasjon for de valgte flaskene, eller la stå tomt for å fjerne plasseringen.</p>
        <LocationPicker value={picked} onChange={setPicked} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Avbryt</Button>
          <Button variant="primary" loading={loading} onClick={() => onConfirm(picked ?? null)}>
            Flytt
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function BulkTagModal({
  open,
  onClose,
  loading,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onConfirm: (tags: string[]) => void;
}) {
  const [text, setText] = useState('');
  const tags = text.split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <Modal open={open} onClose={onClose} title="Legg til tags" size="sm">
      <div className="grid gap-3">
        <Input
          label="Tags (komma-separert)"
          placeholder="favoritt, gave, kelder-2"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Avbryt</Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={tags.length === 0}
            onClick={() => onConfirm(tags)}
          >
            Legg til
          </Button>
        </div>
      </div>
    </Modal>
  );
}
